import React, {useEffect, useState} from 'react';
import {Col, Input, Label, FormFeedback, FormText, UncontrolledTooltip, InputGroup, InputGroupText} from 'reactstrap';
import './FormInput.scss';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleInfo} from '@fortawesome/free-solid-svg-icons'
import {isZipOutOfArea} from '../../../shared/utility';

function FormInput(props) {
    let inputElement;
    const [currentTextContent, setCurrentTextContent] = useState('');
    const [helperFieldId, setHelperFieldId] = useState('');
    const [labelId, setLabelId] = useState('');
    const [helpDetailsId, setHelpDetailsId] = useState('');
    const helpDetailsHiddenId = helpDetailsId ? `${helpDetailsId}-hidden` : undefined;
    let zipOutOfArea = false;

    useEffect(() => {
        if (props.id) {
            setHelperFieldId(`${props.id}-helperText`);
            setLabelId(`${props.id}-label`);
            setHelpDetailsId(`${props.id}-helpDetails`);
        } else {
            setHelperFieldId(`helperText-${crypto.randomUUID()}`);
            setLabelId(`label-${crypto.randomUUID()}`);
            setHelpDetailsId(`helpDetails-${crypto.randomUUID()}`);
        }
    }, [props.id])

    let sharedLabelAttr = {
        name: props.elementConfig.label,
        id: props.name,
        "aria-describedby": helperFieldId,
        "aria-label": props.elementConfig.label,
        "aria-labelledby": labelId
    }

    let calculatedInvalid = props.invalid;
    let calculatedErrorMessage = props.errorMessage;

    // Merge analytics class with potential className coming from elementConfig
    const mergeClassNames = (...classes) => classes.filter(Boolean).join(' ');

    switch (props.elementType) {
        case ('input'): {
            const coreInput = (
                <Input
                    {...props.elementConfig}
                    className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                    invalid={!!(props.invalid && props.touched && props.shouldValidate)}
                    value={props.value}
                    {...sharedLabelAttr}
                    onChange={onChange}
                    valid={!(props.invalid && props.shouldValidate)}/>
            );

            if (props.inlineIcon) {
                inputElement = (
                    <InputGroup>
                        <InputGroupText>
                            <FontAwesomeIcon icon={props.inlineIcon}/>
                        </InputGroupText>
                        {coreInput}
                    </InputGroup>
                );
            } else {
                inputElement = coreInput;
            }
            break;
        }

        case ('textarea'): {
            const coreInput = (
                <Input
                    type="textarea"
                    placeholder={props.elementConfig.placeholder}
                    className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                    invalid={!!(props.invalid && props.touched && props.shouldValidate)}
                    {...sharedLabelAttr}
                    value={props.value}
                    onChange={onChange}
                    rows="8"/>
            );

            if (props.inlineIcon) {
                inputElement = (
                    <InputGroup>
                        <InputGroupText>
                            <FontAwesomeIcon icon={props.inlineIcon}/>
                        </InputGroupText>
                        {coreInput}
                    </InputGroup>
                );
            } else {
                inputElement = coreInput;
            }
            break;
        }

        case ('select'):
            inputElement = (
                <Input
                    type="select"
                    autoComplete={props.elementConfig.autoComplete}
                    className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                    value={props.value}
                    {...sharedLabelAttr}
                    onChange={props.changed}
                    disabled={props.disabled}>

                    {props.elementConfig.options.map(option => (
                        <option key={option.value} value={option.value} disabled={option.disabled}
                                selected={option.selected}>{option.displayValue}</option>
                    ))}

                </Input>
            );
            break;

        case ('file'):
            if (props.elementConfig.multiple) {
                inputElement = (
                    <Input
                        type="file"
                        value={props.value}
                        className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                        {...sharedLabelAttr}
                        onChange={props.changed}
                        multiple/>
                );
            } else {
                inputElement = (
                    <Input
                        type="file"
                        value={props.value}
                        className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                        {...sharedLabelAttr}
                        onChange={props.changed}/>
                );
            }
            break;

        case ('checkbox'):
            inputElement = (
                <Input
                    type="checkbox"
                    value={props.value}
                    className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                    {...sharedLabelAttr}
                    invalid={!!(props.invalid && props.touched && props.shouldValidate)}
                    onChange={props.changed}/>
            );
            break;

        case ('zipcode'): {
            const hasRealValidationError = !!(calculatedInvalid && props.touched);
            const patterns = (props.elementConfig && props.elementConfig.allowedCodes) || [];
            zipOutOfArea = (!hasRealValidationError) && isZipOutOfArea(props.value, patterns);

            const showInvalid = (calculatedInvalid && props.touched && !zipOutOfArea);

            const warningActive = (zipOutOfArea && props.touched);
            const warningInputClass = warningActive ? 'border-warning text-warning anl-warning focus-ring' : '';
            const warningInputStyle = warningActive ? {
                '--bs-focus-ring-color': 'rgba(var(--bs-warning-rgb), .25)',
                '--bs-form-control-focus-border-color': 'var(--bs-warning)'
            } : undefined;
            const mergedClassName = [props.elementConfig?.className, warningInputClass].filter(Boolean).join(' ');
            const mergedStyle = props.elementConfig?.style
                ? {...props.elementConfig.style, ...warningInputStyle}
                : warningInputStyle;

            const coreInput = (
                <Input
                    {...props.elementConfig}
                    invalid={!!showInvalid}
                    className={mergedClassName}
                    style={mergedStyle}
                    value={props.value}
                    {...sharedLabelAttr}
                    onChange={onChange}
                    valid={!showInvalid && props.touched && !zipOutOfArea}
                />
            );

            if (props.inlineIcon) {
                inputElement = (
                    <InputGroup>
                        <InputGroupText>
                            <FontAwesomeIcon icon={props.inlineIcon}/>
                        </InputGroupText>
                        {coreInput}
                    </InputGroup>
                );
            } else {
                inputElement = coreInput;
            }
            break;
        }

        default: {
            const coreInput = (
                <Input
                    {...props.elementConfig}
                    className={mergeClassNames(props.elementConfig?.className, props.analyticsClassName)}
                    {...sharedLabelAttr}
                    value={props.value}
                    onChange={onChange}/>
            );

            if (props.inlineIcon) {
                inputElement = (
                    <InputGroup>
                        <InputGroupText>
                            <FontAwesomeIcon icon={props.inlineIcon}/>
                        </InputGroupText>
                        {coreInput}
                    </InputGroup>
                );
            } else {
                inputElement = coreInput;
            }
            break;
        }
    }

    function onChange(event) {
        setCurrentTextContent(event.target.value);
        props.changed(event);
    }

    let inputGroup;
    const tooltip = props.tooltip ?
        <UncontrolledTooltip placement="bottom" trigger="focus" target={props.name} className="w-100"
                             dangerouslySetInnerHTML={createMarkup(props.tooltip)}/> : null;

    let lengthIndicator;
    if (props.maxLenght) {
        const lengthIndicatorClasses = ['form-text', 'form-input-length'];
        if (props.elementType === 'zipcode' && zipOutOfArea && props.touched) {
            lengthIndicatorClasses.push('text-warning');
        } else if (currentTextContent.length > props.maxLenght) {
            lengthIndicatorClasses.push('text-danger');
        }
        lengthIndicator = <>
            <small
                className={lengthIndicatorClasses.join(' ')}
                aria-hidden={true}>{currentTextContent.length} / {props.maxLenght} Zeichen</small>
            <small
                className="visually-hidden">{`${currentTextContent.length} von ${props.maxLenght} Zeichen verwedet.`}</small>
            <small className="visually-hidden" aria-live="assertive"
                   role="status">{`Noch ${props.maxLenght - currentTextContent.length} Zeichen übrig`}</small>
        </>;
    } else {
        lengthIndicator = <small className="form-input-length-placeholder">&#8288;</small>;
    }

    function createMarkup(text) {
        return {__html: text};
    }

    let helperText;

    // Do not show helper text for out-of-area ZIPs, as the banner outside the input will handle the hint.
    if (props.elementType === 'zipcode' && zipOutOfArea && props.touched) {
        helperText = null;
    } else if (calculatedInvalid && props.touched) {
        helperText = <FormFeedback className="form-error-message" style={{display: 'inline-block'}}
                                   id={helperFieldId}>{calculatedErrorMessage}</FormFeedback>
    } else if (!calculatedInvalid && props.touched) {
        helperText = <small className="form-text text-success" id={helperFieldId}>{props.validMessage}</small>
    } else {
        helperText = <FormText id={helperFieldId}>{props.helperText}</FormText>
    }

    let HelpDetails = () => <></>;

    if (props.helpDetails) {
        HelpDetails = () => <>
            <FontAwesomeIcon id={helpDetailsId} icon={faCircleInfo}
                             className="mx-1 align-self-baseline"/>
            {helpDetailsId &&
                <UncontrolledTooltip target={helpDetailsId}
                                     container="root"
                                     dangerouslySetInnerHTML={createMarkup(props.helpDetails)}
                                     autohide={false}/>}
            <div className="visually-hidden" dangerouslySetInnerHTML={createMarkup(props.helpDetails)}/>
        </>
    }

    if (props.elementType === 'checkbox') {
        inputGroup = (
            <Col className="mb-3" md={props.elementConfig.columns}>
                <div className="form-check">
                    {inputElement}
                    <Label check for={sharedLabelAttr.id}
                           dangerouslySetInnerHTML={createMarkup(props.elementConfig.label)}
                           id={labelId}/>
                    {helperText}
                </div>
            </Col>
        )
    } else {
        inputGroup = (
            <Col className={(props.wrapperCSSClass || 'mb-3')} md={props.elementConfig.columns}>
                <div className="form-text-input">
                    <Label for={sharedLabelAttr.id} id={labelId}>{props.elementConfig.label}<HelpDetails/></Label>
                    {inputElement}
                    <span aria-live="assertive" role="status">{helperText}</span>
                    {lengthIndicator}
                    {props.formText ? <FormText>{props.formText}</FormText> : null}
                    {tooltip}
                </div>
            </Col>
        )
    }

    return (
        <>
            {inputGroup}
        </>
    );
}

export default FormInput;